import {
  Button,
  ButtonGroup,
  closeCurrentDialog,
  Form,
  FormButton,
  FormField,
  FormSubmitHandler,
  showNotification,
} from 'platform/components';
import {VStack} from 'platform/foundation';
import {z} from 'zod';

import {useState} from 'react';

import {isNil} from 'ramda';

import {ListCampaignsApiResponse, useAddVehicleToCampaignMutation} from '@dms/api/sales';
import i18n from '@dms/i18n';
import {handleApiError} from '@dms/shared';

import {Nullish, RequiredTestIdProps, suffixTestId} from 'shared/web';

import {useConfirmRepublishAds} from '../utils/confirmRepublishAds';

const addVehicleToCampaignFormSchema = z.object({
  campaignId: z.string().uuid(),
});

type AddVehicleToCampaignFormValues = z.infer<typeof addVehicleToCampaignFormSchema>;

interface AddVehicleToCampaignFormProps extends RequiredTestIdProps {
  vehicleId: string;
  campaigns: ListCampaignsApiResponse;
  appliedCampaignPosition: number | Nullish;
}

export function AddVehicleToCampaignForm(props: AddVehicleToCampaignFormProps) {
  const [addVehicleToCampaign, {isLoading}] = useAddVehicleToCampaignMutation();
  const confirmRepublishAds = useConfirmRepublishAds();
  const [isCheckingRepublish, setIsCheckingRepublish] = useState(false);

  const campaignOptions = props.campaigns.map((campaign) => ({
    label: campaign.name,
    value: campaign.id,
  }));

  const handleAddVehicleToCampaign = (
    campaignId: string,
    republishAds?: boolean,
    onSuccess?: () => void
  ) =>
    addVehicleToCampaign({campaignId, vehicleId: props.vehicleId, republishAds})
      .unwrap()
      .then(() => {
        showNotification.success(
          republishAds ? i18n.t('entity.vehicle.labels.classifiedsWillBeUpdated') : undefined
        );
        onSuccess?.();
      })
      .catch(handleApiError);

  const willCampaignBecomeApplied = (campaignId: string) => {
    const campaign = props.campaigns.find((item) => item.id === campaignId);

    if (isNil(campaign) || campaign.status !== 'ACTIVE') {
      return false;
    }

    // new applied campaign position is higher than the current applied campaign position, so it will become the new applied campaign and republish ads will be needed
    return (
      isNil(props.appliedCampaignPosition) || campaign.position < props.appliedCampaignPosition
    );
  };

  const handleSubmit: FormSubmitHandler<AddVehicleToCampaignFormValues> = async (values) => {
    if (!willCampaignBecomeApplied(values.campaignId)) {
      await handleAddVehicleToCampaign(values.campaignId, undefined, closeCurrentDialog);
      return;
    }

    setIsCheckingRepublish(true);
    return confirmRepublishAds(
      props.vehicleId,
      (republishAds) => handleAddVehicleToCampaign(values.campaignId, republishAds),
      closeCurrentDialog
    );
  };

  return (
    <Form<AddVehicleToCampaignFormValues>
      experimentalZodSchema={addVehicleToCampaignFormSchema}
      onSubmit={handleSubmit}
    >
      {(control) => (
        <VStack spacing={4}>
          <FormField
            control={control}
            name="campaignId"
            type="choice"
            label={i18n.t('entity.salesCampaign.labels.campaign')}
            options={campaignOptions}
            menuInPortal
            isRequired
            data-testid={suffixTestId('campaign', props)}
          />
          <ButtonGroup align="right">
            <Button
              variant="secondary"
              onClick={closeCurrentDialog}
              isDisabled={isCheckingRepublish}
              title={i18n.t('general.actions.cancel')}
              data-testid={suffixTestId('cancel', props)}
            />
            <FormButton
              control={control}
              isLoading={isLoading || isCheckingRepublish}
              type="submit"
              title={i18n.t('general.actions.add')}
              data-testid={suffixTestId('submit', props)}
            />
          </ButtonGroup>
        </VStack>
      )}
    </Form>
  );
}
